import { Stack, Typography } from '@mui/material';
import React, { useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Button } from '@/shared/components/ui/button/button';
import { TConfirmDialogModalProps } from '@/shared/components/ui/confirm-dialog/components/confirm-dialog-modal/confirm-dialog-modal.types';
import { IconContainer } from '@/shared/components/ui/icon-container/icon-container.styled';
import { ModalContainer } from '@/shared/components/ui/modal-controller/modal-controller.styled';
import { showToastError } from '@/shared/utils/toast';

export const ConfirmDialogModal: React.FC<TConfirmDialogModalProps> = ({
  mutation,
  onSuccess,
  onClose,
  headline,
  primaryButtonLabel,
  primaryButtonVariant = 'primary',
  additionalContent,
  successMessage,
  errorMessage,
  icon,
  disableConfirm,
}) => {
  const { t } = useTranslation();
  const [isLoading, startTransition] = useTransition();
  const buttonLabel = primaryButtonLabel ?? t('common:continue');
  const _successMessage = successMessage ?? t('ui:done');
  const _errorMessage = errorMessage ?? t('common:error');

  const onDelete = async (event: React.BaseSyntheticEvent) => {
    event.stopPropagation();
    startTransition(async () => {
      try {
        await mutation();
        toast.success(_successMessage);
        onSuccess?.();
        onClose(event);
      } catch {
        showToastError(_errorMessage);
      }
    });
  };

  const onCancel = (event: React.BaseSyntheticEvent) => {
    event.stopPropagation();
    onClose(event);
  };

  const renderIcon = () => {
    if (!icon) {
      return null;
    }

    return (
      <IconContainer
        $color={primaryButtonVariant?.includes('alert') ? 'red' : 'blue'}
      >
        {icon}
      </IconContainer>
    );
  };

  const renderHeadline = () => {
    if (typeof headline === 'string') {
      return (
        <Typography variant="h2" textAlign="center">
          {headline}
        </Typography>
      );
    }

    return headline;
  };

  return (
    <ModalContainer alignItems="center" gap="1.5rem" width="440px">
      {renderIcon()}
      {renderHeadline()}
      {additionalContent}
      <Stack direction="row" width="100%" gap="0.75rem">
        <Button
          disabled={isLoading}
          onClick={onCancel}
          fullWidth
          variant="secondary"
        >
          {t('common:cancel')}
        </Button>
        <Button
          fullWidth
          onClick={onDelete}
          loading={isLoading}
          variant={primaryButtonVariant}
          disabled={disableConfirm}
        >
          {buttonLabel}
        </Button>
      </Stack>
    </ModalContainer>
  );
};
